/**
 * Apex Food Source Dashboard Page
 *
 * Contract manufacturing and co-packing operations dashboard for Apex Food Source.
 * Shows DoD/WoW/MoM/QoQ/YoY comparisons for manufacturing, quality, and client KPIs.
 * Uses comprehensive data from apexData.ts for SLM training compatibility.
 * 25+ years experience in Abbotsford BC - food co-packer and contract manufacturer.
 */
import { useState } from 'react';
import {
  Card,
  Text,
  Group,
  Stack,
  Badge,
  SimpleGrid,
  RingProgress,
  Table,
  Select,
  Progress,
  Accordion,
} from '@mantine/core';
import {
  IconBuildingFactory2,
  IconUsers,
  IconChartBar,
  IconPackage,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconClipboardCheck,
  IconClock,
  IconMapPin,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';
import apexLogo from '../assets/logos/apex-food-logo.png';
import {
  APEX_SEGMENTS,
  APEX_METRICS,
  APEX_PRODUCTION_LINES,
  APEX_QUALITY,
  getTopClients,
  getBottomClients,
  type TimePeriod,
} from '../data/apexData';

const formatCurrency = (value: number, compact = false): string => {
  if (compact) {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatChange = (value: number, invert = false) => {
  const isPositive = invert ? value < 0 : value > 0;
  const isNeutral = value === 0;
  const color = isPositive ? 'green' : isNeutral ? 'gray' : 'red';
  const Icon = isPositive ? IconTrendingUp : isNeutral ? IconMinus : IconTrendingDown;

  return (
    <Group gap={4}>
      <Icon size={14} color={isPositive ? '#16a34a' : isNeutral ? '#6b7280' : '#dc2626'} />
      <Text size="sm" c={color} fw={500}>
        {value > 0 ? '+' : ''}{value.toFixed(1)}%
      </Text>
    </Group>
  );
};

const formatChangeAbs = (value: number, invert = false) => {
  const isPositive = invert ? value < 0 : value > 0;
  const color = isPositive ? 'green' : value === 0 ? 'gray' : 'red';
  const prefix = value > 0 ? '+' : '';
  return (
    <Text span c={color} fw={500} size="sm">
      {prefix}{value}
    </Text>
  );
};

export default function ApexFoodPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('MoM');

  const topClients = getTopClients(5);
  const bottomClients = getBottomClients(5);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8f9fa',
      }}
    >
      <TopNav />

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <Stack gap="lg">
          {/* Header with Logo */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <img src={apexLogo} alt="Apex Food Source" style={{ height: 48, width: 'auto' }} />
                <div>
                  <Text size="sm" c="dimmed" style={{ fontFamily: 'Inter, sans-serif' }}>{APEX_METRICS.totalClients} clients | Contract Manufacturing & Co-Packing | Since 1998</Text>
                </div>
              </Group>
            </div>
            <Group gap="md">
              <Select
                value={selectedPeriod}
                onChange={(v) => setSelectedPeriod(v as TimePeriod)}
                data={[
                  { value: 'DoD', label: 'Day over Day' },
                  { value: 'WoW', label: 'Week over Week' },
                  { value: 'MoM', label: 'Month over Month' },
                  { value: 'QoQ', label: 'Quarter over Quarter' },
                  { value: 'YoY', label: 'Year over Year' },
                ]}
                size="sm"
                w={180}
              />
            </Group>
          </Group>

          {/* Key Financial Metrics */}
          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Revenue</Text>
                <IconChartBar size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(APEX_METRICS.totalRevenue.current, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(APEX_METRICS.totalRevenue[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>EBITDA</Text>
                <IconTrendingUp size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(APEX_METRICS.ebitda.current, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(APEX_METRICS.ebitda[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Cases Produced</Text>
                <IconPackage size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{(APEX_METRICS.unitsProduced.current / 1000000).toFixed(2)}M</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(APEX_METRICS.unitsProduced[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Active Clients</Text>
                <IconUsers size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{APEX_METRICS.totalClients}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChangeAbs(selectedPeriod === 'DoD' ? 0 : selectedPeriod === 'WoW' ? 0 : selectedPeriod === 'MoM' ? 1 : selectedPeriod === 'QoQ' ? 2 : 6)}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Lead Time</Text>
                <IconClock size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{APEX_METRICS.leadTimeDays.current} days</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(APEX_METRICS.leadTimeDays[selectedPeriod], true)}
              </Group>
            </Card>
          </SimpleGrid>

          {/* Manufacturing KPIs */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Manufacturing Performance</Text>
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: APEX_METRICS.oee.current, color: '#22c55e' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {APEX_METRICS.oee.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">OEE</Text>
                <Text size="xs" c="dimmed">Equipment Effectiveness</Text>
                <Group justify="center" mt={4}>
                  {formatChange(APEX_METRICS.oee[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: APEX_METRICS.batchYield.current, color: '#3b82f6' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {APEX_METRICS.batchYield.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">Batch Yield</Text>
                <Text size="xs" c="dimmed">Production yield</Text>
                <Group justify="center" mt={4}>
                  {formatChange(APEX_METRICS.batchYield[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: APEX_METRICS.fillRate.current, color: '#f59e0b' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {APEX_METRICS.fillRate.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">Fill Rate</Text>
                <Text size="xs" c="dimmed">Order fulfillment</Text>
                <Group justify="center" mt={4}>
                  {formatChange(APEX_METRICS.fillRate[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: APEX_METRICS.firstPassRate.current, color: '#8b5cf6' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {APEX_METRICS.firstPassRate.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">First Pass Rate</Text>
                <Text size="xs" c="dimmed">Quality rate</Text>
                <Group justify="center" mt={4}>
                  {formatChange(APEX_METRICS.firstPassRate[selectedPeriod])}
                </Group>
              </div>
            </SimpleGrid>
          </Card>

          {/* Client Segment Performance with Expandable Clients */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Client Segment Performance</Text>
            <Accordion chevronPosition="left" variant="separated">
              {APEX_SEGMENTS.map((segment) => (
                <Accordion.Item key={segment.segment} value={segment.segment}>
                  <Accordion.Control>
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Group gap="xs">
                        <IconMapPin size={16} color="#6b7280" />
                        <Text fw={600} size="sm">{segment.segment}</Text>
                        <Badge size="sm" variant="light">{segment.clientCount} clients</Badge>
                      </Group>
                      <Group justify="flex-end" gap="xl" style={{ width: '100%', marginTop: '8px' }}>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '120px' }}>
                          <Text size="xs" c="dimmed">Revenue</Text>
                          <Text size="sm" fw={600}>{formatCurrency(segment.revenue.current, true)}</Text>
                        </Stack>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '140px' }}>
                          <Text size="xs" c="dimmed">Growth Rate</Text>
                          <Badge color={segment.revenue[selectedPeriod] > 3.5 ? 'green' : segment.revenue[selectedPeriod] > 2.5 ? 'yellow' : 'blue'} variant="light">
                            +{segment.revenue[selectedPeriod]}% {selectedPeriod}
                          </Badge>
                        </Stack>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '100px' }}>
                          <Text size="xs" c="dimmed">Orders YTD</Text>
                          <Text size="sm" fw={600}>{segment.ordersYTD}</Text>
                        </Stack>
                      </Group>
                    </Stack>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Client</Table.Th>
                          <Table.Th>City</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Volume (Cases)</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Contract</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {segment.clients.slice(0, 10).map((client) => (
                          <Table.Tr key={client.id}>
                            <Table.Td>
                              <Group gap="xs">
                                {client.isTopPerformer && <Badge size="xs" color="green">Top</Badge>}
                                {client.isBottomPerformer && <Badge size="xs" color="red">Low</Badge>}
                                <Text size="sm">{client.name}</Text>
                              </Group>
                            </Table.Td>
                            <Table.Td><Text size="sm">{client.city}</Text></Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(client.revenue.current, true)}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatChange(client.revenue[selectedPeriod])}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{client.volumeCases.current.toLocaleString()}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Badge color={client.contractType === 'Exclusive' ? 'green' : client.contractType === 'Preferred' ? 'blue' : 'gray'} variant="light">
                                {client.contractType}
                              </Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                    {segment.clients.length > 10 && (
                      <Text size="xs" c="dimmed" mt="xs" ta="center">
                        Showing 10 of {segment.clients.length} clients
                      </Text>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Top Clients */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Top Clients</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="green">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Client</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {topClients.map((client, idx) => (
                    <Table.Tr key={client.id}>
                      <Table.Td style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge color={idx === 0 ? 'yellow' : 'gray'} size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{client.name}</Text>
                          <Text size="xs" c="dimmed">{client.segment}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(client.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(client.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            {/* Bottom Clients */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Needs Attention</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="red">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Client</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bottomClients.map((client, idx) => (
                    <Table.Tr key={client.id}>
                      <Table.Td style={{ display: 'flex', justifyContent: 'center' }}>
                        <Badge color="red" size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{client.name}</Text>
                          <Text size="xs" c="dimmed">{client.segment}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(client.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(client.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Production Lines */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Production Line Performance</Text>
              <Group justify="center" mb="md">
                <IconBuildingFactory2 size={20} color="#6b7280" />
              </Group>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Line</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>OEE</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {APEX_PRODUCTION_LINES.map((line) => (
                    <Table.Tr key={line.name}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{line.name}</Text>
                        <Text size="xs" c="dimmed">{(line.output.current / 1000).toFixed(0)}K cases</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(line.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Badge color={line.oee >= 85 ? 'green' : line.oee >= 80 ? 'yellow' : 'red'}>
                          {line.oee}%
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(line.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            {/* Quality & Compliance */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Quality & Food Safety</Text>
              <Group justify="center" mb="md">
                <IconClipboardCheck size={20} color="#6b7280" />
              </Group>
              <Stack gap="md">
                {APEX_QUALITY.map((item) => (
                  <div key={item.metric}>
                    <Group justify="space-between" mb={4}>
                      <Text size="sm" fw={500}>{item.metric}</Text>
                      <Badge color={item.status === 'Perfect' ? 'green' : item.status === 'Excellent' ? 'blue' : 'yellow'}>
                        {item.status}
                      </Badge>
                    </Group>
                    <Group gap="xs">
                      <Progress
                        value={item.metric.includes('Complaints') ? 100 - (item.score / item.target) * 100 : (item.score / 100) * 100}
                        size="sm"
                        color={item.status === 'Perfect' ? 'green' : item.status === 'Excellent' ? 'blue' : 'yellow'}
                        style={{ flex: 1 }}
                      />
                      <Text size="xs" c="dimmed" style={{ minWidth: 60, textAlign: 'right' }}>
                        {item.metric.includes('Incidents') ? item.score : `${item.score}${item.metric.includes('ppm') ? ' ppm' : '%'}`}
                      </Text>
                    </Group>
                  </div>
                ))}
              </Stack>
            </Card>
          </SimpleGrid>

          {/* Performance Trends */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Performance Trends</Text>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Metric</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Current</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>DoD</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>WoW</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>MoM</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>QoQ</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>YoY</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Revenue</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(APEX_METRICS.totalRevenue.current, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.totalRevenue.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.totalRevenue.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.totalRevenue.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.totalRevenue.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.totalRevenue.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>EBITDA</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(APEX_METRICS.ebitda.current, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.ebitda.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.ebitda.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.ebitda.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.ebitda.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.ebitda.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Cases Produced</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{(APEX_METRICS.unitsProduced.current / 1000000).toFixed(2)}M</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.unitsProduced.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.unitsProduced.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.unitsProduced.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.unitsProduced.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.unitsProduced.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>OEE</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{APEX_METRICS.oee.current}%</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.oee.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.oee.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.oee.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.oee.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.oee.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Batch Yield</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{APEX_METRICS.batchYield.current}%</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.batchYield.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.batchYield.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.batchYield.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.batchYield.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.batchYield.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Cost Per Unit</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>${APEX_METRICS.costPerUnit.current}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.costPerUnit.DoD, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.costPerUnit.WoW, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.costPerUnit.MoM, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.costPerUnit.QoQ, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.costPerUnit.YoY, true)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Lead Time (days)</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{APEX_METRICS.leadTimeDays.current}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.leadTimeDays.DoD, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.leadTimeDays.WoW, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.leadTimeDays.MoM, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.leadTimeDays.QoQ, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>{formatChange(APEX_METRICS.leadTimeDays.YoY, true)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
